import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, Loader2, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface LeadOption {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
}

interface LinkAppointmentLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  appointmentId?: string;
  appointmentDate: string;
  durationMinutes: number;
  googleEventId?: string | null;
  onLinked: () => void;
}

/**
 * Vincula um compromisso avulso (criado manualmente ou importado do Google)
 * a um lead existente — habilitando lembretes de agendamento por WhatsApp.
 */
export function LinkAppointmentLeadDialog({
  open,
  onOpenChange,
  workspaceId,
  appointmentId,
  appointmentDate,
  durationMinutes,
  googleEventId,
  onLinked,
}: LinkAppointmentLeadDialogProps) {
  const { toast } = useToast();
  const [query, setQuery] = useState("");
  const [leads, setLeads] = useState<LeadOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !workspaceId) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      let req = supabase
        .from("leads")
        .select("id, name, email, phone")
        .eq("workspace_id", workspaceId)
        .order("updated_at", { ascending: false })
        .limit(30);

      const term = query.trim();
      if (term) {
        req = req.or(`name.ilike.%${term}%,phone.ilike.%${term}%,email.ilike.%${term}%`);
      }

      const { data } = await req;
      if (!cancelled) {
        setLeads((data as LeadOption[]) || []);
        setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, workspaceId, query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelectedId("");
    }
  }, [open]);

  const handleLink = async () => {
    if (!selectedId || !appointmentId) return;
    setSaving(true);
    try {
      // Preserva o metadata existente e marca o vínculo como manual para que a
      // sincronização com o Google Calendar não sobrescreva a correção.
      const { data: current } = await supabase
        .from("appointments")
        .select("metadata")
        .eq("id", appointmentId)
        .eq("workspace_id", workspaceId)
        .maybeSingle();
      const currentMeta =
        current?.metadata && typeof current.metadata === "object" && !Array.isArray(current.metadata)
          ? (current.metadata as Record<string, unknown>)
          : {};

      const { error } = await supabase
        .from("appointments")
        .update({
          lead_id: selectedId,
          metadata: { ...currentMeta, auto_linked: false, manual_link: true, auto_unlinked_reason: null },
        })
        .eq("id", appointmentId)
        .eq("workspace_id", workspaceId);
      if (error) throw error;


      // Espelha no lead para que os lembretes e o agente reconheçam o agendamento.
      const isFuture = new Date(appointmentDate).getTime() > Date.now();
      if (isFuture) {
        await supabase
          .from("leads")
          .update({
            current_appointment_date: appointmentDate,
            current_appointment_duration: durationMinutes,
            ...(googleEventId ? { current_appointment_id: googleEventId } : {}),
          })
          .eq("id", selectedId)
          .eq("workspace_id", workspaceId);
      }

      toast({
        title: "Agendamento vinculado",
        description: isFuture
          ? "O lead receberá os lembretes configurados."
          : "Compromisso vinculado ao lead.",
      });
      onOpenChange(false);
      onLinked();
    } catch (err) {
      console.error("Error linking appointment to lead:", err);
      toast({
        title: "Erro ao vincular",
        description: "Não foi possível vincular o agendamento ao lead.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary" />
            Vincular a um lead
          </DialogTitle>
          <DialogDescription>
            Ao vincular, os lembretes de agendamento passam a ser enviados para o lead.
          </DialogDescription>
        </DialogHeader>

        <Command shouldFilter={false} className="border rounded-md">
          <CommandInput
            placeholder="Buscar lead por nome, telefone ou email..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>{loading ? "Carregando..." : "Nenhum lead encontrado."}</CommandEmpty>
            <CommandGroup>
              {leads.map((lead) => (
                <CommandItem key={lead.id} value={lead.id} onSelect={() => setSelectedId(lead.id)}>
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      selectedId === lead.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <div className="min-w-0">
                    <div className="font-medium">{lead.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {[lead.phone, lead.email].filter(Boolean).join(" · ") || "Sem contato"}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleLink} disabled={!selectedId || saving || !appointmentId}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Vincular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
